import moment from 'moment';
import { useDateTimeFormat } from './useDateTimeFormat';

export const MEETING_REPEAT_TYPE = {
  NOT_REPEAT: 'Does not repeat',
  WEEKLY: 'Weekly',
  BIWEEKLY: 'Biweekly'
};

export const useMeetingGenerator = () => {
  const { addedDate, formatedDatetime } = useDateTimeFormat();
  /**
   * Generate all online meetings from UI input data
   *
   * Pass arguments as Object with all followed keys
   * @param {String} start_at date time in UTC format (2013-02-04T10:35:24-08:00)
   * @param {String} end_at date time in UTC format (2013-02-04T10:35:24-08:00)
   * @param {String} meeting_repeat_type 'Does not repeat' / 2 = 'Weekly' / 3 = 'Biweekly'
   * @param {String[]} meeting_days Days of week for meeting
   * @param {String} meeting_start_time time with format HH:mm (12:00)
   * @returns {Array} date list of meetings
   */
  const generateMeeting = ({
    start_at,
    end_at,
    meeting_repeat_type,
    meeting_days,
    meeting_start_time
  }) => {
    let result = [];

    /* Guard clause */
    if (
      meeting_repeat_type !== MEETING_REPEAT_TYPE.NOT_REPEAT &&
      meeting_repeat_type !== MEETING_REPEAT_TYPE.WEEKLY &&
      meeting_repeat_type !== MEETING_REPEAT_TYPE.BIWEEKLY
    ) {
      throw new Error(
        "Invalid Argument: meeting repeat type should be 'Does not repeat', 'Weekly' or 'BiWeekly'"
      );
    }

    if (start_at && end_at && meeting_repeat_type && meeting_days.length && meeting_start_time) {
      /* No Repeat */
      if (meeting_repeat_type == MEETING_REPEAT_TYPE.NOT_REPEAT) {
        result = generateNotRepeat(start_at, end_at, meeting_start_time, meeting_days);
      }

      /* Weekly */
      if (meeting_repeat_type === MEETING_REPEAT_TYPE.WEEKLY) {
        result = generateWeekly(start_at, end_at, meeting_start_time, meeting_days);
      }

      /* Bi-weekly */
      if (meeting_repeat_type === MEETING_REPEAT_TYPE.BIWEEKLY) {
        result = generateBiWeekly(start_at, end_at, meeting_start_time, meeting_days);
      }
    }

    return result;
  };

  const generateNotRepeat = (start_at, end_at, meeting_start_time, meeting_days) => {
    const result = [];

    const startToEndDifference = moment(end_at).diff(moment(start_at), 'days');

    for (let day = 0; day <= startToEndDifference; day++) {
      const date = `${formatedDatetime(
        addedDate(start_at, day),
        'YYYY-MM-DD'
      )}T${meeting_start_time}`;

      const dayOfDate = formatedDatetime(addedDate(start_at, day), 'dddd');
      /* meeting_days (days of week) is part of repeat days
                start section date equal to meeting date & meeting time start before section time
                end section date equal to meeting date & meeting time start after section time */
      if (
        meeting_days.includes(dayOfDate) &&
        moment(date).isSameOrAfter(start_at, 'minute') &&
        moment(end_at).diff(date, 'minutes') >= 60
      )
        result.push(date);
      /* break for loop when meeting lists are full */
      if (result.length === meeting_days.length) break;
    }

    return result;
  };

  const generateWeekly = (start_at, end_at, meeting_start_time, meeting_days) => {
    const result = [];

    const startToEndDifference = moment(end_at).diff(moment(start_at), 'days');

    for (let day = 0; day <= startToEndDifference; day++) {
      const date = `${formatedDatetime(
        addedDate(start_at, day),
        'YYYY-MM-DD'
      )}T${meeting_start_time}`;

      const dayOfDate = formatedDatetime(addedDate(start_at, day), 'dddd');

      if (
        meeting_days.includes(dayOfDate) &&
        moment(date).isSameOrAfter(start_at, 'minute') &&
        moment(end_at).diff(date, 'minutes') >= 60
      )
        result.push(date);
    }

    return result;
  };

  const generateBiWeekly = (start_at, end_at, meeting_start_time, meeting_days) => {
    let result = [];

    const startToEndDifference = moment(end_at).diff(moment(start_at), 'days');

    const sectionStartDateOfWeek = moment(new Date(start_at)).startOf('week').format('YYYY-MM-DD');

    let biweeklyDaySteps = 14;

    for (
      let biweeklyDay = 0;
      biweeklyDay <= startToEndDifference;
      biweeklyDay += biweeklyDaySteps
    ) {
      biweeklyDaySteps = 14;

      const startDateInWeek = formatedDatetime(
        addedDate(sectionStartDateOfWeek, biweeklyDay),
        'YYYY-MM-DD'
      );

      let currentWeekMeeting = generateBiWeeklySubWeek(
        start_at,
        end_at,
        meeting_start_time,
        meeting_days,
        startDateInWeek,
        0,
        7
      );

      // If in the current week doesn't found any matched days, move to the next week to find furthur
      if (!currentWeekMeeting.length) {
        let nextWeekMeeting = generateBiWeeklySubWeek(
          start_at,
          end_at,
          meeting_start_time,
          meeting_days,
          startDateInWeek,
          7,
          14
        );

        currentWeekMeeting = [...currentWeekMeeting, ...nextWeekMeeting];

        biweeklyDaySteps = 21;
      }

      currentWeekMeeting = currentWeekMeeting.splice(0, meeting_days.length);

      result = [...result, ...currentWeekMeeting];
    }

    return result;
  };

  const generateBiWeeklySubWeek = (
    start_at,
    end_at,
    meeting_start_time,
    meeting_days,
    startDateInWeek,
    startDay,
    endDay
  ) => {
    let result = [];

    for (let day = startDay; day < endDay; day++) {
      const dateInWeek = `${formatedDatetime(
        addedDate(startDateInWeek, day),
        'YYYY-MM-DD'
      )}T${meeting_start_time}`;

      const dayOfDate = formatedDatetime(addedDate(startDateInWeek, day), 'dddd');

      if (
        meeting_days.includes(dayOfDate) &&
        moment(dateInWeek).isSameOrAfter(start_at, 'minute') &&
        moment(end_at).diff(dateInWeek, 'minutes') >= 60
      ) {
        result.push(dateInWeek);
      }
    }

    return result;
  };

  return {
    generateMeeting
  };
};
